import React, { useEffect, useId, useRef, useState } from 'react';
import { productIsAlcoholic, type MachineProduct } from '../api';
import { t } from '../i18n';

type AlcoholBadgeProps = {
  product: MachineProduct;
  className?: string;
};

/** Alcoholic / non-alcoholic chip whenever the API gives a clear signal. */
export const AlcoholBadge = ({ product, className = '' }: AlcoholBadgeProps) => {
  const alcoholic = productIsAlcoholic(product);
  if (alcoholic === undefined) return null;

  return (
    <span
      className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide ${
        alcoholic
          ? 'border-[rgba(232,168,90,0.5)] bg-[rgba(232,168,90,0.16)] text-[#e8a85a]'
          : 'border-[rgba(242,244,243,0.22)] bg-[rgba(255,255,255,0.06)] text-[var(--muted-lt)]'
      } ${className}`}
    >
      {alcoholic ? t.alcoholicLabel : t.nonAlcoholicLabel}
    </span>
  );
};

export function productDescription(description?: string | null): string | null {
  const trimmed = description?.trim() ?? '';
  return trimmed || null;
}

type DescriptionInfoProps = {
  description?: string | null;
  /** Extra classes for the absolute top-right info button. */
  buttonClassName?: string;
  children: React.ReactNode;
};

/**
 * Wraps a product card: ⓘ sits top-right whenever a description exists.
 * Tap toggles the blurb under the card body (works on phones — no hover).
 */
export const DescriptionInfo = ({
  description,
  buttonClassName = '',
  children,
}: DescriptionInfoProps) => {
  const text = productDescription(description);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex h-full w-full flex-col">
      {text && (
        <button
          type="button"
          aria-label={t.drinkInfoAria}
          aria-expanded={open}
          aria-controls={panelId}
          className={`touch-target absolute right-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
            open
              ? 'border-[var(--border-act)] bg-[var(--accent-bg)] text-[var(--gold-lt)]'
              : 'border-[var(--border)] bg-[rgba(0,0,0,0.35)] text-[var(--muted-lt)]'
          } ${buttonClassName}`}
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen((v) => !v);
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
            <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 7.25v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="5" r="0.85" fill="currentColor" />
          </svg>
        </button>
      )}

      {children}

      {text && open && (
        <p
          id={panelId}
          role="note"
          className="border-t border-[var(--border)] bg-[rgba(255,255,255,0.03)] px-3 py-2.5 text-left text-[0.8rem] leading-snug text-[var(--muted-lt)]"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {text}
        </p>
      )}
    </div>
  );
};
